import React, { useEffect, useMemo, useState } from "react";
import { Box, MenuItem, Select } from "@mui/material";
import {
  tokens,
  fmtNumber,
  SignalBar,
  StatusPill,
  ClinicalDataGrid,
  ClinicalPageShell,
} from "../../shared/clinical-design";
import OutreachDetailPanel from "../components/OutreachDetailPanel";
import { getOutreachQueue } from "../services/outreachService";
import {
  LOCATIONS,
  DEFAULT_LOCATION,
  HEALTH_STATUS_META,
  TIMEFRAME_META,
} from "../common/constants";

const COLUMNS = [
  { key: "name", header: "Member" },
  {
    key: "timeSinceVisit",
    header: "Time since visit",
    render: (r) => (
      <StatusPill
        tone={TIMEFRAME_META[r.timeSinceVisit]?.tone || "muted"}
        label={r.timeSinceVisit || "Unknown"}
        dense
      />
    ),
  },
  {
    key: "contactability",
    header: "Phone / Email",
    width: 110,
    render: (r) => (
      <Box component="span" sx={{ fontFamily: tokens.fontMono, fontSize: 12.5 }}>
        <Box component="span" sx={{ color: r.hasPhone ? tokens.ok : tokens.crit }}>
          {r.hasPhone ? "PH ✓" : "PH ✗"}
        </Box>
        {"  "}
        <Box component="span" sx={{ color: r.hasEmail ? tokens.ok : tokens.crit }}>
          {r.hasEmail ? "EM ✓" : "EM ✗"}
        </Box>
      </Box>
    ),
  },
  {
    key: "healthStatus",
    header: "Health status",
    render: (r) => (
      <StatusPill
        tone={HEALTH_STATUS_META[r.healthStatus]?.tone || "muted"}
        label={r.healthStatus || "Unknown"}
        dense
      />
    ),
  },
  {
    key: "conditions",
    header: "Conditions",
    render: (r) => (
      <Box
        component="span"
        sx={{
          fontSize: 12.5,
          color: tokens.ink2,
          display: "inline-block",
          maxWidth: 220,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {r.conditions || "—"}
      </Box>
    ),
  },
];

/**
 * Module H — Outreach Console (Member Outreach Campaign Engine).
 * Cola de miembros sin visita reciente con contactabilidad, y detalle con log
 * de intentos de contacto (estado local) cerrando el loop hasta la cita.
 */
export default function MemberOutreachPage() {
  const [locationKey, setLocationKey] = useState(DEFAULT_LOCATION);
  const [data, setData] = useState({ stats: {}, patients: [] });
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getOutreachQueue(locationKey)
      .then((d) => {
        if (!active) return;
        setData(d);
        setSelectedId(d.patients[0]?.cipId ?? null);
      })
      .catch(() => {
        if (!active) return;
        setData({ stats: {}, patients: [] });
        setSelectedId(null);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [locationKey]);

  const selectedRow = useMemo(
    () => data.patients.find((p) => p.cipId === selectedId) || null,
    [data, selectedId]
  );

  const signals = useMemo(() => {
    const s = data.stats;
    const rows = data.patients;
    const toContact = s.patientsToContact ?? rows.length;
    const contactable = s.contactable ?? rows.filter((r) => r.hasPhone || r.hasEmail).length;
    const notContactable = rows.filter((r) => !r.hasPhone && !r.hasEmail).length;
    const over12m = rows.filter((r) => r.timeSinceVisit === "12+ Months").length;
    const phonePct = rows.length
      ? Math.round((rows.filter((r) => r.hasPhone).length / rows.length) * 100)
      : 0;
    const emailPct = rows.length
      ? Math.round((rows.filter((r) => r.hasEmail).length / rows.length) * 100)
      : 0;
    return [
      { label: "Members to contact", value: fmtNumber(toContact), tone: "info" },
      { label: "12+ months no visit", value: fmtNumber(over12m), tone: over12m > 0 ? "crit" : "ok" },
      { label: "Contactable", value: fmtNumber(contactable), tone: "ok" },
      {
        label: "Not contactable",
        value: fmtNumber(notContactable),
        tone: notContactable > 0 ? "crit" : "ok",
        hint: "Members without phone or email on file — need demographic update",
      },
      { label: "With phone", value: `${phonePct}%`, tone: "muted" },
      { label: "With email", value: `${emailPct}%`, tone: "muted" },
    ];
  }, [data]);

  const locationLabel =
    LOCATIONS.find((l) => l.key === locationKey)?.label || locationKey;

  return (
    <ClinicalPageShell
      title="Outreach Console"
      subtitle="Members without a recent visit, prioritized by time since last visit and clinical severity. Log contact attempts and close the loop to a scheduled visit."
      moduleTag="Module H"
      devPreview
      actions={
        <Select
          size="small"
          value={locationKey}
          onChange={(e) => setLocationKey(e.target.value)}
          sx={{ fontSize: 13, minWidth: 170, bgcolor: tokens.surface }}
        >
          {LOCATIONS.map((l) => (
            <MenuItem key={l.key} value={l.key} sx={{ fontSize: 13 }}>
              {l.label}
            </MenuItem>
          ))}
        </Select>
      }
    >
      <SignalBar signals={signals} sx={{ mb: 1.5 }} />
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          alignItems: "flex-start",
          flexDirection: { xs: "column", lg: "row" },
        }}
      >
        <Box sx={{ width: { xs: "100%", lg: "45%" }, flexShrink: 0 }}>
          <ClinicalDataGrid
            columns={COLUMNS}
            rows={data.patients}
            rowKey={(r) => r.cipId}
            loading={loading}
            onRowClick={(r) => setSelectedId(r.cipId)}
            isRowSelected={(r) => r.cipId === selectedId}
            emptyMessage="No members pending outreach for this location."
            maxHeight={620}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
          <OutreachDetailPanel
            key={selectedRow?.cipId ?? "none"}
            row={selectedRow}
            locationLabel={locationLabel}
          />
        </Box>
      </Box>
    </ClinicalPageShell>
  );
}
